import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AlertCircle, CalendarRange, ChevronDown, ChevronRight, FileSpreadsheet, FileText, IndianRupee, Layers, Lock, MapPin, RefreshCw, Wallet, X } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { toast } from "sonner";
import { KpiCard, PageHeader } from "./common";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Skeleton } from "./ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { api, downloadBlob, errMsg, fmtINR } from "../lib/api";
import { useLiveCollections } from "../lib/liveCollections";
import { EMPTY_AREA, areaParams, areaLabel, areaActive } from "./FieldActivityTable";
import { TerritoryFilterSelects, useTerritoryMasters } from "./TerritoryFilter";

/**
 * Phase 230 — Head-wise Collection Report (shared by the Admin page, the zone-scoped Supervisor page and the collector's
 * "My Reports" section). One row per tax head — User Charges, Property Tax, Water Tax ... — with total demand, collected,
 * outstanding and the realised %, a collected-vs-outstanding bar per head, and every row opening into four breakdowns:
 * by financial year, by zone & ward, by payment mode and by tax collector. Excel / PDF downloads when `can_download`.
 *
 * Figures come from GET /t/{code}/reports/head-wise (server-side territory scoping for supervisors / collectors).
 */
const COLLECTED = "hsl(152,55%,32%)";
const OUTSTANDING = "hsl(38,92%,45%)";
const ALL_FY = "__all__";
/** Server alias (core/head_wise.py): the financial year flagged "current" in Master Data — the report's default view. */
export const CURRENT_FY = "current";

export const PctBar = ({ pct, testId, narrow = false }) => (
  <div className={`flex items-center gap-2 ${narrow ? "min-w-[84px]" : "min-w-[110px]"}`} data-testid={testId}>
    <div className="h-2 flex-1 rounded-full bg-muted overflow-hidden" aria-hidden="true">
      <div className="h-full rounded-full bg-[hsl(var(--success))]" style={{ width: `${Math.min(100, Math.max(0, pct || 0))}%` }} />
    </div>
    <span className="text-xs tabular-nums w-12 text-right">{(pct ?? 0).toFixed(1)}%</span>
  </div>
);

const Money = ({ v, className = "" }) => <span className={`tabular-nums ${className}`}>{fmtINR(v || 0)}</span>;

function BreakdownTable({ rows, columns, testId, emptyText = "Nothing to show" }) {
  if (!rows?.length) return <p className="text-xs text-muted-foreground py-3 px-1" data-testid={`${testId}-empty`}>{emptyText}</p>;
  return (
    <div className="overflow-x-auto rounded-lg border bg-card">
      <Table data-testid={testId}>
        <TableHeader>
          <TableRow>
            {columns.map((c) => <TableHead key={c.key} className={`${c.align === "right" ? "text-right" : ""} text-xs h-9`}>{c.label}</TableHead>)}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r, i) => (
            <TableRow key={i} className="odd:bg-muted/20" data-testid={`${testId}-row-${i}`}>
              {columns.map((c) => (
                <TableCell key={c.key} className={`py-2 text-sm ${c.align === "right" ? "text-right" : ""}`}>
                  {c.render ? c.render(r) : r[c.key]}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function HeadBreakdown({ head, collectedLabel, testId }) {
  const money = (k) => ({ align: "right", render: (r) => <Money v={r[k]} /> });
  return (
    <Tabs defaultValue="fy" className="w-full" data-testid={testId}>
      <TabsList className="h-9 flex-wrap">
        <TabsTrigger value="fy" className="text-xs" data-testid={`${testId}-tab-fy`}>By year</TabsTrigger>
        <TabsTrigger value="area" className="text-xs" data-testid={`${testId}-tab-area`}>By zone & ward</TabsTrigger>
        <TabsTrigger value="mode" className="text-xs" data-testid={`${testId}-tab-mode`}>By payment mode</TabsTrigger>
        <TabsTrigger value="collector" className="text-xs" data-testid={`${testId}-tab-collector`}>By collector</TabsTrigger>
      </TabsList>
      <TabsContent value="fy" className="mt-3">
        <BreakdownTable testId={`${testId}-fy`} rows={head.by_fy} columns={[
          { key: "fy_label", label: "Financial year", render: (r) => <span className="font-medium">{r.fy_label}</span> },
          { key: "properties", label: "Properties", align: "right" },
          { key: "demand", label: "Demand", ...money("demand") },
          { key: "collected", label: collectedLabel, ...money("collected") },
          { key: "outstanding", label: "Outstanding", ...money("outstanding") },
          { key: "pct", label: "Realised", align: "right", render: (r) => <PctBar pct={r.pct} /> },
        ]} />
      </TabsContent>
      <TabsContent value="area" className="mt-3">
        <BreakdownTable testId={`${testId}-area`} rows={head.by_area} columns={[
          { key: "zone", label: "Zone", render: (r) => <span className="font-medium">{r.zone}</span> },
          { key: "ward", label: "Ward" },
          { key: "properties", label: "Properties", align: "right" },
          { key: "demand", label: "Demand", ...money("demand") },
          { key: "collected", label: collectedLabel, ...money("collected") },
          { key: "outstanding", label: "Outstanding", ...money("outstanding") },
          { key: "pct", label: "Realised", align: "right", render: (r) => <PctBar pct={r.pct} /> },
        ]} />
      </TabsContent>
      <TabsContent value="mode" className="mt-3">
        <BreakdownTable testId={`${testId}-mode`} rows={head.by_mode} emptyText="No receipts under this head yet" columns={[
          { key: "mode", label: "Payment mode", render: (r) => <span className="font-medium">{r.mode}</span> },
          { key: "receipts", label: "Receipts", align: "right" },
          { key: "amount", label: collectedLabel, ...money("amount") },
          { key: "share", label: "Share", align: "right", render: (r) => <span className="tabular-nums text-xs">{r.share.toFixed(1)}%</span> },
        ]} />
      </TabsContent>
      <TabsContent value="collector" className="mt-3">
        <BreakdownTable testId={`${testId}-collector`} rows={head.by_collector} emptyText="No receipts under this head yet" columns={[
          { key: "collector", label: "Collected by", render: (r) => <span className="font-medium">{r.collector}{r.employee_id ? <span className="text-muted-foreground font-normal text-xs"> ({r.employee_id})</span> : null}</span> },
          { key: "receipts", label: "Receipts", align: "right" },
          { key: "amount", label: collectedLabel, ...money("amount") },
          { key: "share", label: "Share", align: "right", render: (r) => <span className="tabular-nums text-xs">{r.share.toFixed(1)}%</span> },
        ]} />
      </TabsContent>
    </Tabs>
  );
}

export function HeadWiseReport({
  code, title = "Head-wise Collection", subtitle = "Demand, collected and outstanding per tax head",
  showArea = true, lockedZoneIds = null, scopeZoneNames = [], areaLevels = ["zone", "ward", "mohalla"],
  canDownload: canDownloadProp = true, compact = false, hideHeader = false, testIdPrefix = "head-wise",
  defaultFy = CURRENT_FY,   // opens on the current financial year; "All years" and every other year stay one click away
}) {
  const T = testIdPrefix;
  const locked = Array.isArray(lockedZoneIds) && lockedZoneIds.length > 0;
  const baseArea = useMemo(() => (locked && lockedZoneIds.length === 1 ? { ...EMPTY_AREA, zoneId: lockedZoneIds[0] } : EMPTY_AREA), [locked, lockedZoneIds]);
  const [area, setAreaRaw] = useState(baseArea);
  useEffect(() => { setAreaRaw(baseArea); }, [baseArea]);
  const setArea = (next) => setAreaRaw(locked && lockedZoneIds.length === 1 ? { ...EMPTY_AREA, ...(next || {}), zoneId: lockedZoneIds[0] } : { ...EMPTY_AREA, ...(next || {}) });
  const masters = useTerritoryMasters(code, { withSupervisors: showArea && areaLevels.includes("supervisor") });
  const [fy, setFy] = useState(defaultFy);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState({});
  const [busy, setBusy] = useState(null);

  const params = useMemo(() => ({
    ...(fy !== ALL_FY ? { fy_id: fy } : {}),
    ...(dateFrom ? { date_from: dateFrom } : {}),
    ...(dateTo ? { date_to: dateTo } : {}),
    ...(showArea ? areaParams(area) : {}),
  }), [fy, dateFrom, dateTo, area, showArea]);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const r = await api.get(`/t/${code}/reports/head-wise`, { params });
      setData(r.data);
    } catch (e) {
      setError(errMsg(e));
      if (!silent) setData(null);
    } finally {
      setLoading(false);
    }
  }, [code, params]);
  useEffect(() => { load(); }, [load]);
  useLiveCollections(code, () => load(true));   // Phase 149: figures follow the live ledger

  const download = async (format) => {
    setBusy(format);
    try {
      const qs = new URLSearchParams({ format, ...params }).toString();
      const fyTag = data?.filters?.fy_label ? `_FY${data.filters.fy_label}` : "";
      await downloadBlob(`/t/${code}/reports/head-wise/export?${qs}`, `${code}_head-wise${fyTag}.${format}`);
      toast.success(`Head-wise report downloaded (${format === "pdf" ? "PDF" : "Excel"})`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(null);
    }
  };

  const heads = data?.heads || [];
  const totals = data?.totals;
  const period = !!data?.filters?.period;
  const fyLabel = data?.filters?.fy_label || null;
  const collectedLabel = period ? "Collected (period)" : "Collected";
  const canDownload = canDownloadProp && data?.can_download !== false;
  const label = showArea ? areaLabel(masters, area) : "";
  const active = showArea && areaActive(area);
  const narrowed = locked ? !!(area.supervisorId || area.wardId || area.mohallaId || (lockedZoneIds.length > 1 && area.zoneId)) : active;
  const filtersActive = fy !== defaultFy || !!dateFrom || !!dateTo || narrowed;
  const chartRows = heads.map((h) => ({ name: h.tax_head, Collected: h.collected, Outstanding: h.outstanding }));
  const fyOptions = data?.fy_options || [];
  const currentFyId = fyOptions.find((f) => f.is_current)?.id || null;
  // The Select shows the resolved year for the "current" alias; "" keeps the placeholder until the first load answers.
  const fySelectValue = fy === CURRENT_FY ? (currentFyId || (data ? ALL_FY : "")) : fy;
  const toggle = (id) => setOpen((o) => ({ ...o, [id]: !o[id] }));
  const resetAll = () => { setFy(defaultFy); setDateFrom(""); setDateTo(""); setArea(baseArea); };

  return (
    <div data-testid={`${T}-report`} className="space-y-4">
      {!hideHeader && (
        <PageHeader title={title} subtitle={subtitle}>
          <Button variant="outline" size="sm" onClick={() => load()} disabled={loading} data-testid={`${T}-refresh`}>
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          {canDownload && (
            <>
              <Button size="sm" onClick={() => download("pdf")} disabled={!!busy || loading} data-testid={`${T}-download-pdf`}>
                <FileText className="h-3.5 w-3.5 mr-1.5" /> {busy === "pdf" ? "Preparing..." : "PDF"}
              </Button>
              <Button size="sm" variant="outline" onClick={() => download("xlsx")} disabled={!!busy || loading} data-testid={`${T}-download-xlsx`}>
                <FileSpreadsheet className="h-3.5 w-3.5 mr-1.5" /> {busy === "xlsx" ? "Preparing..." : "Excel"}
              </Button>
            </>
          )}
        </PageHeader>
      )}

      {/* ---------- filters ---------- */}
      <Card className="shadow-sm" data-testid={`${T}-filters`}>
        <CardContent className="p-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Financial year</Label>
              <Select value={fySelectValue} onValueChange={setFy}>
                <SelectTrigger className="h-10" data-testid={`${T}-fy-select`}><SelectValue placeholder="Current financial year" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_FY} data-testid={`${T}-fy-all`}>All years</SelectItem>
                  {fyOptions.map((f) => <SelectItem key={f.id} value={f.id} data-testid={`${T}-fy-${f.label}`}>{f.label}{f.is_current ? " (current)" : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Receipts from</Label>
              <Input type="date" className="h-10" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid={`${T}-date-from`} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Receipts to</Label>
              <Input type="date" className="h-10" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid={`${T}-date-to`} />
            </div>
          </div>
          {showArea && (
            <div className="flex flex-col lg:flex-row lg:items-end gap-3">
              <div className="flex-1">
                <TerritoryFilterSelects masters={masters} value={area} onChange={setArea} testIdPrefix={`${T}-area`} lockedZoneIds={lockedZoneIds} levels={areaLevels} />
              </div>
              <Button variant="outline" className="h-10" onClick={resetAll} disabled={!filtersActive} data-testid={`${T}-clear`}>
                <X className="h-4 w-4 mr-1.5" /> Clear
              </Button>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground" data-testid={`${T}-filter-status`}>
            {locked && (
              <Badge variant="outline" className="gap-1 font-normal bg-primary/10 text-primary border-primary/20" data-testid={`${T}-scope-badge`}>
                <Lock className="h-3 w-3" /> Your zone{scopeZoneNames.length > 1 ? "s" : ""}: {scopeZoneNames.filter(Boolean).join(", ") || "mapped zone"}
              </Badge>
            )}
            {data && (
              <Badge variant="outline" className="gap-1 font-normal" data-testid={`${T}-fy-badge`}>
                <CalendarRange className="h-3 w-3" /> {fyLabel ? `FY ${fyLabel}` : "All years"}
              </Badge>
            )}
            {narrowed && <Badge variant="outline" className="gap-1 font-normal bg-accent text-accent-foreground border-transparent" data-testid={`${T}-area-badge`}><MapPin className="h-3 w-3" /> {label}</Badge>}
            <span>
              {period
                ? <>"{collectedLabel}" counts receipts dated {dateFrom || "the start"} to {dateTo || "today"}; demand and outstanding are today's ledger figures.</>
                : <>"Collected" is everything paid to date on these demands; the realised % is collected ÷ demand.</>}
            </span>
            {!showArea && !compact && <Button variant="ghost" size="sm" className="h-7 px-2" onClick={resetAll} disabled={!filtersActive} data-testid={`${T}-clear`}>Clear filters</Button>}
          </div>
        </CardContent>
      </Card>

      {/* ---------- KPIs ---------- */}
      {error && !data ? (
        <Card className="shadow-sm"><CardContent className="p-6 flex flex-col items-center gap-2 text-center" role="alert" data-testid={`${T}-error`}>
          <AlertCircle className="h-5 w-5 text-destructive" />
          <p className="text-sm text-destructive">{error}</p>
          <Button size="sm" variant="outline" onClick={() => load()} data-testid={`${T}-retry`}><RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Retry</Button>
        </CardContent></Card>
      ) : loading && !data ? (
        <div className={`grid grid-cols-2 ${compact ? "" : "lg:grid-cols-4"} gap-3`} data-testid={`${T}-skeleton`}>
          {[0, 1, 2, 3].map((i) => <Card key={i} className="shadow-sm"><CardContent className="p-4 space-y-3"><Skeleton className="h-3 w-24" /><Skeleton className="h-7 w-32" /></CardContent></Card>)}
        </div>
      ) : (
        <>
          <div className={`grid grid-cols-2 ${compact ? "" : "lg:grid-cols-4"} gap-3`} data-testid={`${T}-kpis`}>
            <KpiCard label="Total demand" value={totals?.demand || 0} money icon={IndianRupee} testId={`${T}-kpi-demand`} sub={`${totals?.heads || 0} tax head${totals?.heads === 1 ? "" : "s"} · ${totals?.properties || 0} properties`} />
            <KpiCard label={collectedLabel} value={totals?.collected || 0} money icon={Wallet} testId={`${T}-kpi-collected`} sub={period ? `${totals?.receipts || 0} receipts in the period` : `${(totals?.pct || 0).toFixed(1)}% realised`} />
            <KpiCard label="Outstanding" value={totals?.outstanding || 0} money icon={AlertCircle} testId={`${T}-kpi-outstanding`} sub={totals?.demand > 0 ? `${(100 - (totals?.pct || 0)).toFixed(1)}% of demand still due` : undefined} />
            <KpiCard label="Largest head" value={heads[0]?.tax_head || "—"} icon={Layers} testId={`${T}-kpi-top-head`} sub={heads[0] ? `${heads[0].share_of_demand.toFixed(1)}% of demand · ${fmtINR(heads[0].outstanding)} due` : undefined} />
          </div>

          {/* ---------- chart + table ---------- */}
          <Card className="shadow-sm" data-testid={`${T}-card`}>
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-heading flex items-center gap-2"><Layers className="h-4 w-4 text-muted-foreground" /> Collected vs outstanding by tax head</CardTitle>
              <CardDescription>Click a tax head to open its year, zone &amp; ward, payment-mode and collector breakdowns.{totals?.unattributed > 0 ? ` ${fmtINR(totals.unattributed)} of receipts in scope is advance credit not tied to a demand and is not split by head.` : ""}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {heads.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-10" data-testid={`${T}-empty`}>No demands match these filters.</p>
              ) : (
                <>
                  {!compact && (
                    <div style={{ height: Math.max(140, 44 * heads.length + 40) }} data-testid={`${T}-chart`}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartRows} layout="vertical" margin={{ left: 8, right: 16, top: 4, bottom: 4 }} barSize={18}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e9ef" horizontal={false} />
                          <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => (v >= 100000 ? `${(v / 100000).toFixed(1)}L` : v >= 1000 ? `${Math.round(v / 1000)}k` : v)} />
                          <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 12 }} />
                          <Tooltip formatter={(v) => fmtINR(v)} cursor={{ fill: "hsl(206,88%,28%)", fillOpacity: 0.06 }} />
                          <Legend />
                          <Bar dataKey="Collected" stackId="a" fill={COLLECTED} radius={[4, 0, 0, 4]} />
                          <Bar dataKey="Outstanding" stackId="a" fill={OUTSTANDING} radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                  <div className="overflow-x-auto rounded-lg border">
                    <Table data-testid={`${T}-table`}>
                      <TableHeader className="sticky top-0 bg-card z-10">
                        <TableRow>
                          <TableHead className="w-8" />
                          <TableHead className={compact ? "min-w-[120px]" : ""}>Tax head</TableHead>
                          {!compact && <TableHead className="text-right hidden sm:table-cell">Properties</TableHead>}
                          <TableHead className="text-right">Demand</TableHead>
                          <TableHead className="text-right">{collectedLabel}</TableHead>
                          <TableHead className="text-right">Outstanding</TableHead>
                          <TableHead className={`text-right ${compact ? "w-28" : "w-36"}`}>Realised</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {heads.map((h) => {
                          const id = h.tax_head_id || "none";
                          const isOpen = !!open[id];
                          return (
                            <Fragment key={id}>
                              <TableRow className="cursor-pointer hover:bg-muted/60 odd:bg-muted/20" onClick={() => toggle(id)} data-testid={`${T}-row-${h.tax_code || id}`} data-state={isOpen ? "open" : "closed"}
                                role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggle(id); } }} aria-expanded={isOpen}>
                                <TableCell className="py-2.5 pr-0 text-muted-foreground">{isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</TableCell>
                                <TableCell className="py-2.5">
                                  <p className="font-medium leading-tight" data-testid={`${T}-row-name-${h.tax_code || id}`}>{h.tax_head}</p>
                                  <p className="text-[11px] text-muted-foreground">
                                    {compact
                                      ? `${h.share_of_demand.toFixed(1)}% of demand · ${h.properties} propert${h.properties === 1 ? "y" : "ies"}`
                                      : `${h.tax_code ? `${h.tax_code} · ` : ""}${h.share_of_demand.toFixed(1)}% of demand · ${h.demands} demand${h.demands === 1 ? "" : "s"}`}
                                  </p>
                                </TableCell>
                                {!compact && <TableCell className="py-2.5 text-right tabular-nums hidden sm:table-cell">{h.properties}</TableCell>}
                                <TableCell className="py-2.5 text-right"><Money v={h.demand} /></TableCell>
                                <TableCell className="py-2.5 text-right font-medium text-[hsl(var(--success))]"><Money v={h.collected} /></TableCell>
                                <TableCell className="py-2.5 text-right font-medium text-destructive"><Money v={h.outstanding} /></TableCell>
                                <TableCell className="py-2.5"><PctBar pct={h.pct} narrow={compact} testId={`${T}-row-pct-${h.tax_code || id}`} /></TableCell>
                              </TableRow>
                              {isOpen && (
                                <TableRow className="bg-muted/30 hover:bg-muted/30" data-testid={`${T}-detail-${h.tax_code || id}`}>
                                  <TableCell colSpan={compact ? 6 : 7} className="p-3 sm:p-4">
                                    <HeadBreakdown head={h} collectedLabel={collectedLabel} testId={`${T}-breakdown-${h.tax_code || id}`} />
                                  </TableCell>
                                </TableRow>
                              )}
                            </Fragment>
                          );
                        })}
                        {totals && (
                          <TableRow className="bg-muted/40 font-semibold" data-testid={`${T}-total-row`}>
                            <TableCell />
                            <TableCell className="py-2.5">Total</TableCell>
                            {!compact && <TableCell className="py-2.5 text-right tabular-nums hidden sm:table-cell">{totals.properties}</TableCell>}
                            <TableCell className="py-2.5 text-right"><Money v={totals.demand} /></TableCell>
                            <TableCell className="py-2.5 text-right text-[hsl(var(--success))]"><Money v={totals.collected} /></TableCell>
                            <TableCell className="py-2.5 text-right text-destructive"><Money v={totals.outstanding} /></TableCell>
                            <TableCell className="py-2.5"><PctBar pct={totals.pct} narrow={compact} testId={`${T}-total-pct`} /></TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </>
              )}
              {hideHeader && canDownload && heads.length > 0 && (
                <div className="flex gap-2 justify-end">
                  <Button size="sm" onClick={() => download("pdf")} disabled={!!busy} data-testid={`${T}-download-pdf`}><FileText className="h-3.5 w-3.5 mr-1.5" /> {busy === "pdf" ? "Preparing..." : "PDF"}</Button>
                  <Button size="sm" variant="outline" onClick={() => download("xlsx")} disabled={!!busy} data-testid={`${T}-download-xlsx`}><FileSpreadsheet className="h-3.5 w-3.5 mr-1.5" /> {busy === "xlsx" ? "Preparing..." : "Excel"}</Button>
                </div>
              )}
              {data && !canDownload && (
                <p className="flex items-center gap-1.5 text-xs text-muted-foreground" data-testid={`${T}-downloads-locked`}><Lock className="h-3 w-3" /> Downloads need Full access from your administrator.</p>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

/** Phase 230 — dashboard card: one tile per tax head (collected, outstanding, demand, realised-% bar) + a link to the full report.
 *  Defaults to the current financial year like the report itself; `fy` accepts a year id, CURRENT_FY or null (all years). */
export function HeadWiseSummaryCard({ code, href, fy = CURRENT_FY, className = "", testId = "dashboard-head-wise" }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const load = useCallback(() => {
    setError(false);
    api.get(`/t/${code}/reports/head-wise`, { params: fy ? { fy_id: fy } : {} }).then((r) => setData(r.data)).catch(() => setError(true));
  }, [code, fy]);
  useEffect(() => { load(); }, [load]);
  useLiveCollections(code, load);
  const heads = data?.heads || [];
  const t = data?.totals;
  const fyLabel = data?.filters?.fy_label;
  return (
    <Card className={`shadow-sm ${className}`} data-testid={testId}>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <Layers className="h-4 w-4 text-muted-foreground" /> Collection by tax head
              {data && <Badge variant="outline" className="font-normal text-xs" data-testid={`${testId}-fy`}>{fyLabel ? `FY ${fyLabel}` : "All years"}</Badge>}
            </CardTitle>
            <CardDescription className="mt-1" data-testid={`${testId}-summary`}>
              {t ? <>{fmtINR(t.collected)} collected of {fmtINR(t.demand)} demand across {t.heads} tax head{t.heads === 1 ? "" : "s"} · {t.pct.toFixed(1)}% realised · {fmtINR(t.outstanding)} outstanding</> : "Demand, collected and outstanding per tax head"}
            </CardDescription>
          </div>
          <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
            <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-sm" style={{ background: COLLECTED }} aria-hidden="true" /> Collected</span>
            <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-sm" style={{ background: OUTSTANDING }} aria-hidden="true" /> Outstanding</span>
            {href && <Button asChild variant="outline" size="sm" className="h-7 px-2 text-xs"><Link to={href} data-testid={`${testId}-open`}>Full report <ChevronRight className="h-3.5 w-3.5 ml-0.5" /></Link></Button>}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {error ? (
          <div className="flex flex-col items-center gap-2 py-6 text-center" data-testid={`${testId}-error`}>
            <AlertCircle className="h-5 w-5 text-destructive" /><p className="text-sm text-muted-foreground">Could not load this section.</p>
            <Button size="sm" variant="outline" onClick={load} data-testid={`${testId}-retry`}><RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Retry</Button>
          </div>
        ) : !data ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3" aria-hidden="true">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-24 w-full" />)}</div>
        ) : heads.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8" data-testid={`${testId}-empty`}>No demand data{fyLabel ? ` for FY ${fyLabel}` : ""} yet</p>
        ) : (
          <ul className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3" data-testid={`${testId}-list`}>
            {heads.map((h) => {
              const total = (h.collected || 0) + (h.outstanding || 0);
              const w = total > 0 ? Math.min(100, (h.collected / total) * 100) : 0;
              const key = h.tax_code || h.tax_head_id || "none";
              const inner = (
                <>
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-medium leading-tight truncate" title={h.tax_head}>{h.tax_head}</p>
                    <span className="shrink-0 text-xs font-semibold tabular-nums text-[hsl(var(--success))]" data-testid={`${testId}-pct-${key}`}>{(h.pct ?? 0).toFixed(1)}%</span>
                  </div>
                  <p className="mt-1 text-lg font-semibold tabular-nums leading-tight" data-testid={`${testId}-collected-${key}`}>{fmtINR(h.collected)}</p>
                  <div className="mt-2 h-2 w-full rounded-full overflow-hidden flex bg-muted" aria-hidden="true">
                    <div className="h-full" style={{ width: `${w}%`, background: COLLECTED }} />
                    <div className="h-full" style={{ width: `${100 - w}%`, background: OUTSTANDING, opacity: 0.85 }} />
                  </div>
                  <p className="mt-1.5 text-[11px] text-muted-foreground tabular-nums">
                    <span className="text-destructive font-medium">{fmtINR(h.outstanding)}</span> outstanding of {fmtINR(h.demand)} · {h.properties} propert{h.properties === 1 ? "y" : "ies"}
                  </p>
                </>
              );
              const cls = "block rounded-lg border bg-card p-3 h-full";
              return (
                <li key={key} data-testid={`${testId}-row-${key}`}>
                  {href
                    ? <Link to={href} className={`${cls} hover:border-primary/50 hover:shadow-sm transition-shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring`} title={`Open the head-wise report (${h.tax_head})`}>{inner}</Link>
                    : <div className={cls}>{inner}</div>}
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

export default HeadWiseReport;
